import { ALL_APP_LOCALES, DEFAULT_LOCALE, type AppLocale } from "@/lib/i18n/constants";
import { getCanonicalRouteDefinitions } from "@/lib/routes/registry";

export type LocalizedSlugRoutingResolution =
  | {
      readonly type: "redirect";
      readonly locale: AppLocale;
      readonly routePath: string;
      readonly destinationPath: string;
      readonly status: 301;
    }
  | {
      readonly type: "rewrite";
      readonly locale: AppLocale;
      readonly routePath: string;
      readonly destinationPath: string;
    };

type NonDefaultLocale = Exclude<AppLocale, "en">;

const PROTECTED_SEGMENTS = new Set([
  "18",
  "2026",
  "aeo",
  "ai",
  "amp",
  "api",
  "aup",
  "b2b",
  "bing",
  "chatgpt",
  "copilot",
  "eeat",
  "fanly",
  "fansly",
  "funnel",
  "planner",
  "fanvue",
  "geo",
  "gemini",
  "google",
  "instagram",
  "lora",
  "onlyfans",
  "perplexity",
  "pornhub",
  "ppc",
  "reddit",
  "seo",
  "tiktok",
  "ymyl",
  "youtube",
]);

const ENGLISH_SLUG_LOCALES = new Set<AppLocale>(["ar", "he"]);
const LEGACY_NATIVE_SLUG_LOCALES = ["ar", "he"] as const;

const NEW_LOCALE_SEGMENT_TRANSLATIONS: Partial<Record<NonDefaultLocale, Record<string, string>>> = {
  de: {
    about: "ueber-uns",
    accessibility: "barrierefreiheit",
    "acceptable-use": "akzeptable-nutzung",
    adult: "adult-marketing",
    agencies: "agenturen",
    blog: "blog",
    blogs: "blog",
    "blog-detail": "artikel",
    "case-studies": "fallstudien",
    compare: "vergleich",
    contact: "kontakt",
    cookies: "cookies",
    creators: "creator",
    "editorial-policy": "redaktionsrichtlinie",
    enterprise: "enterprise",
    "persona-paths": "persona-pfade",
    paths: "pfade",
    resources: "ressourcen",
    support: "support",
    cancellation: "kuendigung",
    checkout: "kasse",
    "manual-transfer": "bankueberweisung",
    services: "leistungen",
    privacy: "datenschutz",
    terms: "bedingungen",
    packages: "pakete",
    portfolio: "portfolio",
    insights: "einblicke",
    industries: "branchen",
    "franchise-multi-location": "franchise-multi-standort",
    franchise: "franchise",
    "ai-avatar-marketing": "ai-avatar-marketing",
    "ai-avatar-video": "ai-avatar-video",
    "ai-search": "ai-suche",
    "ai-video-production": "ai-video-produktion",
    "geo-aeo-ai-search": "geo-aeo-ai-suche",
    "geo-vs-seo": "geo-vs-seo",
    "what-is-aeo": "was-ist-aeo",
    "what-is-geo": "was-ist-geo",
  },
  "pt-br": {
    about: "sobre",
    accessibility: "acessibilidade",
    "acceptable-use": "uso-aceitavel",
    adult: "marketing-adulto",
    agencies: "agencias",
    blog: "blog",
    blogs: "blog",
    "blog-detail": "artigo",
    "case-studies": "estudos-de-caso",
    compare: "comparar",
    contact: "contato",
    cookies: "cookies",
    creators: "criadores",
    "editorial-policy": "politica-editorial",
    enterprise: "empresas",
    "persona-paths": "caminhos-de-personas",
    paths: "caminhos",
    resources: "recursos",
    support: "suporte",
    cancellation: "cancelamento",
    checkout: "pagamento",
    "manual-transfer": "transferencia-bancaria",
    services: "servicos",
    privacy: "privacidade",
    terms: "termos",
    packages: "pacotes",
    portfolio: "portfolio",
    insights: "insights",
    industries: "setores",
    "franchise-multi-location": "franquia-multilocal",
    franchise: "franquia",
    "ai-avatar-marketing": "marketing-avatar-ai",
    "ai-avatar-video": "video-avatar-ai",
    "ai-search": "busca-ai",
    "ai-video-production": "producao-video-ai",
    "geo-aeo-ai-search": "busca-geo-aeo-ai",
    "geo-vs-seo": "geo-vs-seo",
    "what-is-aeo": "o-que-e-aeo",
    "what-is-geo": "o-que-e-geo",
  },
  ja: {
    about: "gaiyo",
    accessibility: "akuseshibiriti",
    "acceptable-use": "riyo-kitei",
    adult: "adult-marketing",
    agencies: "eijenshi",
    blog: "blog",
    blogs: "blog",
    "blog-detail": "kiji",
    "case-studies": "jirei",
    compare: "hikaku",
    contact: "otoiawase",
    cookies: "cookie",
    creators: "kurieta",
    "editorial-policy": "henshu-hoshin",
    enterprise: "hojin",
    "persona-paths": "persona-keiro",
    paths: "keiro",
    resources: "shigen",
    support: "sapoto",
    cancellation: "kyanseru",
    checkout: "kessai",
    "manual-transfer": "ginko-furikomi",
    services: "sabisu",
    privacy: "puraibashi",
    terms: "kiyaku",
    packages: "pakkeji",
    portfolio: "jisseki",
    insights: "insaito",
    industries: "gyokai",
    "franchise-multi-location": "franchise-tatenpo",
    franchise: "furanchizu",
    "ai-avatar-marketing": "ai-avatar-marketing",
    "ai-avatar-video": "ai-avatar-video",
    "ai-search": "ai-kensaku",
    "ai-video-production": "ai-video-seisaku",
    "geo-aeo-ai-search": "geo-aeo-ai-kensaku",
    "geo-vs-seo": "geo-vs-seo",
    "what-is-aeo": "aeo-toha",
    "what-is-geo": "geo-toha",
  },
  hi: {
    about: "hamare-bare-me",
    accessibility: "pahunch-yogyata",
    "acceptable-use": "svikarya-upyog",
    adult: "adult-marketing",
    agencies: "ejensiyan",
    blog: "blog",
    blogs: "blog",
    "blog-detail": "lekh",
    "case-studies": "case-adhyayan",
    compare: "tulana",
    contact: "sampark",
    cookies: "cookies",
    creators: "creators",
    "editorial-policy": "sampadkiya-niti",
    enterprise: "enterprise",
    "persona-paths": "persona-path",
    paths: "path",
    resources: "sansadhan",
    support: "sahayata",
    cancellation: "raddikaran",
    checkout: "bhugtan",
    "manual-transfer": "bank-hastantaran",
    services: "sevaen",
    privacy: "gopniyata",
    terms: "sharten",
    packages: "packages",
    portfolio: "portfolio",
    insights: "insights",
    industries: "udyog",
    "franchise-multi-location": "franchise-multi-location",
    franchise: "franchise",
    "ai-avatar-marketing": "ai-avatar-marketing",
    "ai-avatar-video": "ai-avatar-video",
    "ai-search": "ai-khoj",
    "ai-video-production": "ai-video-utpadan",
    "geo-aeo-ai-search": "geo-aeo-ai-khoj",
    "geo-vs-seo": "geo-vs-seo",
    "what-is-aeo": "aeo-kya-hai",
    "what-is-geo": "geo-kya-hai",
  },
  id: {
    about: "tentang",
    accessibility: "aksesibilitas",
    "acceptable-use": "penggunaan-yang-diizinkan",
    adult: "pemasaran-dewasa",
    agencies: "agensi",
    blog: "blog",
    blogs: "blog",
    "blog-detail": "artikel",
    "case-studies": "studi-kasus",
    compare: "bandingkan",
    contact: "kontak",
    cookies: "cookie",
    creators: "kreator",
    "editorial-policy": "kebijakan-redaksi",
    enterprise: "perusahaan",
    "persona-paths": "jalur-persona",
    paths: "jalur",
    resources: "sumber-daya",
    support: "dukungan",
    cancellation: "pembatalan",
    checkout: "pembayaran",
    "manual-transfer": "transfer-bank",
    services: "layanan",
    privacy: "privasi",
    terms: "ketentuan",
    packages: "paket",
    portfolio: "portofolio",
    insights: "wawasan",
    industries: "industri",
    "franchise-multi-location": "franchise-banyak-lokasi",
    franchise: "waralaba",
    "ai-avatar-marketing": "pemasaran-avatar-ai",
    "ai-avatar-video": "video-avatar-ai",
    "ai-search": "pencarian-ai",
    "ai-video-production": "produksi-video-ai",
    "geo-aeo-ai-search": "pencarian-geo-aeo-ai",
    "geo-vs-seo": "geo-vs-seo",
    "what-is-aeo": "apa-itu-aeo",
    "what-is-geo": "apa-itu-geo",
  },
  "zh-cn": {
    about: "guanyu",
    accessibility: "wuzhangai",
    "acceptable-use": "keyong-zhengce",
    adult: "chengren-yingxiao",
    agencies: "daili-jigou",
    blog: "blog",
    blogs: "blog",
    "blog-detail": "wenzhang",
    "case-studies": "anli",
    compare: "duibi",
    contact: "lianxi",
    cookies: "cookies",
    creators: "chuangzuozhe",
    "editorial-policy": "bianji-zhengce",
    enterprise: "qiye",
    "persona-paths": "renqun-lujing",
    paths: "lujing",
    resources: "ziyuan",
    support: "zhichi",
    cancellation: "quxiao",
    checkout: "jiezhang",
    "manual-transfer": "yinhang-zhuanzhang",
    services: "fuwu",
    privacy: "yinsi",
    terms: "tiaokuan",
    packages: "taocan",
    portfolio: "zuopin",
    insights: "dongcha",
    industries: "hangye",
    "franchise-multi-location": "jiameng-duodian",
    franchise: "jiameng",
    "ai-avatar-marketing": "ai-avatar-yingxiao",
    "ai-avatar-video": "ai-avatar-video",
    "ai-search": "ai-sousuo",
    "ai-video-production": "ai-video-zhizuo",
    "geo-aeo-ai-search": "geo-aeo-ai-sousuo",
    "geo-vs-seo": "geo-vs-seo",
    "what-is-aeo": "shenme-shi-aeo",
    "what-is-geo": "shenme-shi-geo",
  },
  tr: {
    about: "hakkimizda",
    accessibility: "erisilebilirlik",
    "acceptable-use": "kabul-edilebilir-kullanim",
    adult: "yetiskin-pazarlama",
    agencies: "ajanslar",
    blog: "blog",
    blogs: "blog",
    "blog-detail": "makale",
    "case-studies": "vaka-calismalari",
    compare: "karsilastir",
    contact: "iletisim",
    cookies: "cerezler",
    creators: "ureticiler",
    "editorial-policy": "editor-politikasi",
    enterprise: "kurumsal",
    "persona-paths": "persona-yollari",
    paths: "yollar",
    resources: "kaynaklar",
    support: "destek",
    cancellation: "iptal",
    checkout: "odeme",
    "manual-transfer": "banka-havalesi",
    services: "hizmetler",
    privacy: "gizlilik",
    terms: "sartlar",
    packages: "paketler",
    portfolio: "portfoy",
    insights: "icgoruler",
    industries: "sektorler",
    "franchise-multi-location": "franchise-coklu-lokasyon",
    franchise: "bayilik",
    "ai-avatar-marketing": "ai-avatar-pazarlama",
    "ai-avatar-video": "ai-avatar-video",
    "ai-search": "ai-arama",
    "ai-video-production": "ai-video-uretimi",
    "geo-aeo-ai-search": "geo-aeo-ai-arama",
    "geo-vs-seo": "geo-vs-seo",
    "what-is-aeo": "aeo-nedir",
    "what-is-geo": "geo-nedir",
  },
  ko: {
    about: "seoh-gaeyo",
    accessibility: "jeopgeunseong",
    "acceptable-use": "heoyong-sayong",
    adult: "adult-marketing",
    agencies: "agency",
    blog: "blog",
    blogs: "blog",
    "blog-detail": "geul",
    "case-studies": "sarye",
    compare: "bigyo",
    contact: "munui",
    cookies: "cookies",
    creators: "creator",
    "editorial-policy": "pyeonjip-jeongchaek",
    enterprise: "enterprise",
    "persona-paths": "persona-gyeongro",
    paths: "gyeongro",
    resources: "jaryo",
    support: "jiwon",
    cancellation: "chwiso",
    checkout: "gyeolje",
    "manual-transfer": "eunhaeng-iche",
    services: "service",
    privacy: "privacy",
    terms: "terms",
    packages: "package",
    portfolio: "portfolio",
    insights: "insight",
    industries: "saneop",
    "franchise-multi-location": "franchise-dajeompo",
    franchise: "peuraenchaijeu",
    "ai-avatar-marketing": "ai-avatar-marketing",
    "ai-avatar-video": "ai-avatar-video",
    "ai-search": "ai-geomsaek",
    "ai-video-production": "ai-video-jejak",
    "geo-aeo-ai-search": "geo-aeo-ai-geomsaek",
    "geo-vs-seo": "geo-vs-seo",
    "what-is-aeo": "aeo-ran",
    "what-is-geo": "geo-ran",
  },
  it: {
    about: "chi-siamo",
    accessibility: "accessibilita",
    "acceptable-use": "uso-accettabile",
    adult: "marketing-adult",
    agencies: "agenzie",
    blog: "blog",
    blogs: "blog",
    "blog-detail": "articolo",
    "case-studies": "casi-studio",
    compare: "confronta",
    contact: "contatto",
    cookies: "cookie",
    creators: "creator",
    "editorial-policy": "politica-editoriale",
    enterprise: "enterprise",
    "persona-paths": "percorsi-persona",
    paths: "percorsi",
    resources: "risorse",
    support: "supporto",
    cancellation: "cancellazione",
    checkout: "pagamento",
    "manual-transfer": "bonifico-bancario",
    services: "servizi",
    privacy: "privacy",
    terms: "termini",
    packages: "pacchetti",
    portfolio: "portfolio",
    insights: "approfondimenti",
    industries: "settori",
    "franchise-multi-location": "franchise-multi-sede",
    franchise: "franchising",
    "ai-avatar-marketing": "marketing-avatar-ai",
    "ai-avatar-video": "video-avatar-ai",
    "ai-search": "ricerca-ai",
    "ai-video-production": "produzione-video-ai",
    "geo-aeo-ai-search": "ricerca-geo-aeo-ai",
    "geo-vs-seo": "geo-vs-seo",
    "what-is-aeo": "cos-e-aeo",
    "what-is-geo": "cos-e-geo",
  },
};

const SEGMENT_TRANSLATIONS: Partial<Record<NonDefaultLocale, Record<string, string>>> = {
  ...NEW_LOCALE_SEGMENT_TRANSLATIONS,
  ru: {
    about: "o-nas",
    accessibility: "dostupnost",
    "acceptable-use": "pravila-ispolzovaniya",
    adult: "adult-biznes",
    agencies: "agentstva",
    blog: "blog",
    blogs: "blog",
    "blog-detail": "statya",
    "case-studies": "dokazatelstva",
    compare: "sravnenie",
    contact: "kontakty",
    cookies: "fayly-cookie",
    creators: "kreatory",
    "editorial-policy": "redaktsionnaya-politika",
    enterprise: "korporativnyy-seo",
    "persona-paths": "puti-pokupateley",
    paths: "puti",
    "multilingual-ai-search-localization": "mnogoyazychnaya-lokalizatsiya-ai-poiska",
    "ai-search-governance-compliance": "upravlenie-ai-poiskom-i-komplaens",
    "scaling-seo-agency-founder": "osnovatel-rastushchego-seo-agentstva",
    "white-label-seo-reseller": "reseller-white-label-seo",
    "ppc-performance-agency": "ppc-performance-agentstvo",
    "web-ux-accessibility-agency": "web-ux-dostupnost-agentstvo",
    "prompt-ai-content-agency": "agentstvo-ai-kontenta-i-promptov",
    "multilingual-geo-reseller": "reseller-mnogoyazychnogo-geo",
    "ai-video-avatar-studio": "studiya-ai-video-i-avatarov",
    "multi-location-marketing-leader": "rukovoditel-marketinga-multi-location",
    "franchise-geo-strategist": "geo-strateg-franshizy",
    "multi-location-reputation-operator": "operator-reputatsii-multi-location",
    "creator-economy-growth-manager": "menedzher-rosta-creator-economy",
    "creator-ai-video-content-team": "komanda-ai-video-kontenta-dlya-kreatorov",
    "lawful-adult-creator-agency": "legalnoe-adult-creator-agentstvo",
    "adult-platform-compliance-marketing": "komplaens-marketing-adult-platform",
    "regulated-ai-visibility-governance": "upravlenie-reguliruemoy-ai-vidimostyu",
    "enterprise-orm-compliance-advisor": "enterprise-orm-komplaens-konsultant",
    "adult-industry-marketing": "adult-industriya-marketing",
    "adult-orm": "adult-reputatsiya",
    "18-plus-compliance-marketing-checklist": "cheklist-komplaensa-18-plus-marketing",
    "2026-white-label-agency-playbook": "playbook-white-label-agentstva-2026",
    "age-verification": "proverka-vozrasta",
    "ai-avatar-video": "ai-avatar-video",
    "ai-overviews-visibility-audit-template": "shablon-audita-vidimosti-ai-overviews",
    "ai-ready-content-frameworks": "freymvorki-kontenta-dlya-ai",
    "ai-search-product-queries": "produktovye-zaprosy-ai-poiska",
    "ai-video-production": "ai-video-proizvodstvo",
    "ai-video-cost-time-calculator": "kalkulyator-stoimosti-i-vremeni-ai-video",
    app: "app",
    "aup-explainer": "obyasnenie-aup",
    "b2b-saas-geo-aeo-visibility-audit": "audit-vidimosti-b2b-saas-geo-aeo",
    "blog-deatail": "statya-oshibka",
    "category-architecture": "arkhitektura-kategoriy",
    "compliance-posture": "pozitsiya-komplaensa",
    "content-velocity-system": "sistema-skorosti-kontenta",
    "custom-lora-character-models": "kastomnye-lora-personazhi",
    "ecommerce-seo-audit-template": "shablon-audita-ecommerce-seo",
    "fan-platform-growth-playbook": "playbook-rosta-fan-platform",
    "franchise-multi-location": "franshiza-multi-lokatsii",
    franchise: "franshiza",
    "geo-vs-seo": "geo-protiv-seo",
    "hospitality-travel-seo-playbook": "playbook-seo-gostepriimstva-i-puteshestviy",
    industries: "otrasli",
    insights: "stati",
    "mainstream-platform-brand-safe-strategy": "brand-safe-strategiya-mainstream-platform",
    "measuring-ai-citation-success": "izmerenie-uspekha-ai-tsitirovaniy",
    "monetization-aware-seo": "seo-s-uchetom-monetizatsii",
    "multi-location-local-seo-audit-template": "shablon-audita-local-seo-multi-location",
    packages: "pakety",
    "performer-consent": "soglasie-ispolnitelya",
    portfolio: "portfolio",
    privacy: "konfidentsialnost",
    "privacy-policy": "politika-konfidentsialnosti",
    "publisher-topic-authority": "tematicheskiy-avtoritet-izdatelya",
    "regulated-claims-review": "proverka-reguliruemykh-utverzhdeniy",
    resources: "resursy",
    support: "podderzhka",
    cancellation: "otmena",
    checkout: "oplata",
    "manual-transfer": "bankovskiy-perevod",
    "saas-tech": "saas-tekhnologii",
    "schema-for-ai-engines": "schema-dlya-ai-dvizhkov",
    "senior-solo-seo-hiring-brief": "brif-nayma-senior-solo-seo",
    services: "uslugi",
    terms: "usloviya",
    "vs-ai-content-tools": "sravnenie-ai-content-tools",
    "vs-freelancer-marketplaces": "sravnenie-freelance-marketplaces",
    "vs-generic-white-label-seo": "sravnenie-tipovogo-white-label-seo",
    "what-is-aeo": "chto-takoe-aeo",
    "what-is-geo": "chto-takoe-geo",
  },
  ar: {
    about: "من-نحن",
    accessibility: "امكانية-الوصول",
    "acceptable-use": "الاستخدام-المقبول",
    adult: "اعمال-للبالغين",
    agencies: "وكالات",
    blog: "مدونة",
    blogs: "مدونة",
    "blog-detail": "مقال",
    "case-studies": "اثبات-العمل",
    compare: "مقارنة",
    contact: "اتصال",
    cookies: "ملفات-الارتباط",
    creators: "صناع-المحتوى",
    enterprise: "المؤسسات",
    "persona-paths": "مسارات-المشترين",
    paths: "مسارات",
    "multilingual-ai-search-localization": "توطين-بحث-ai-متعدد-اللغات",
    "ai-search-governance-compliance": "حوكمة-بحث-ai-والامتثال",
    "scaling-seo-agency-founder": "مؤسس-وكالة-seo-نامية",
    "white-label-seo-reseller": "موزع-white-label-seo",
    "ppc-performance-agency": "وكالة-ppc-والأداء",
    "web-ux-accessibility-agency": "وكالة-ويب-ux-وإتاحة",
    "prompt-ai-content-agency": "وكالة-محتوى-ai-وبرومبتات",
    "multilingual-geo-reseller": "موزع-geo-متعدد-اللغات",
    "ai-video-avatar-studio": "استوديو-فيديو-ai-وأفاتار",
    "multi-location-marketing-leader": "قائد-تسويق-متعدد-المواقع",
    "franchise-geo-strategist": "استراتيجي-geo-للامتياز",
    "multi-location-reputation-operator": "مشغل-سمعة-متعدد-المواقع",
    "creator-economy-growth-manager": "مدير-نمو-اقتصاد-المبدعين",
    "creator-ai-video-content-team": "فريق-محتوى-فيديو-ai-للمبدعين",
    "lawful-adult-creator-agency": "وكالة-مبدعين-للبالغين-قانونية",
    "adult-platform-compliance-marketing": "تسويق-امتثال-منصات-البالغين",
    "regulated-ai-visibility-governance": "حوكمة-ظهور-ai-منظم",
    "enterprise-orm-compliance-advisor": "مستشار-orm-وامتثال-للمؤسسات",
    "adult-industry-marketing": "تسويق-اعمال-البالغين",
    "adult-orm": "سمعة-البالغين",
    "18-plus-compliance-marketing-checklist": "قائمة-امتثال-تسويق-18-plus",
    "2026-white-label-agency-playbook": "دليل-وكالات-وايت-ليبل-2026",
    "age-verification": "التحقق-من-العمر",
    "ai-avatar-video": "فيديو-افتار-ai",
    "ai-overviews-visibility-audit-template": "قالب-تدقيق-ظهور-ai-overviews",
    "ai-ready-content-frameworks": "اطر-محتوى-جاهز-ai",
    "ai-search-product-queries": "استعلامات-منتجات-بحث-ai",
    "ai-video-production": "انتاج-فيديو-ai",
    "ai-video-cost-time-calculator": "حاسبة-تكلفة-وقت-فيديو-ai",
    app: "تطبيق",
    "aup-explainer": "شرح-aup",
    "b2b-saas-geo-aeo-visibility-audit": "تدقيق-ظهور-b2b-saas-geo-aeo",
    "blog-deatail": "تصحيح-مقال",
    "category-architecture": "هندسة-التصنيفات",
    "compliance-posture": "جاهزية-الامتثال",
    "content-velocity-system": "نظام-سرعة-المحتوى",
    "custom-lora-character-models": "نماذج-شخصيات-lora-مخصصة",
    "ecommerce-seo-audit-template": "قالب-تدقيق-ecommerce-seo",
    "fan-platform-growth-playbook": "دليل-نمو-منصات-المعجبين",
    "franchise-multi-location": "امتياز-متعدد-المواقع",
    franchise: "امتياز",
    "geo-vs-seo": "geo-مقابل-seo",
    "hospitality-travel-seo-playbook": "دليل-seo-للضيافة-والسفر",
    industries: "قطاعات",
    insights: "رؤى",
    "mainstream-platform-brand-safe-strategy": "استراتيجية-امنة-للعلامة-على-المنصات",
    "measuring-ai-citation-success": "قياس-نجاح-استشهادات-ai",
    "monetization-aware-seo": "seo-يراعي-الربح",
    "multi-location-local-seo-audit-template": "قالب-تدقيق-local-seo-متعدد-المواقع",
    packages: "باقات",
    "performer-consent": "موافقة-المؤدي",
    portfolio: "اعمال-سابقة",
    privacy: "الخصوصية",
    "privacy-policy": "سياسة-الخصوصية",
    "publisher-topic-authority": "سلطة-موضوعية-للناشر",
    "regulated-claims-review": "مراجعة-ادعاءات-منظمة",
    resources: "موارد",
    support: "daam",
    cancellation: "ilghaa",
    checkout: "dafaa",
    "manual-transfer": "tahwil-banki",
    "saas-tech": "saas-تقنية",
    "schema-for-ai-engines": "schema-لمحركات-ai",
    "senior-solo-seo-hiring-brief": "موجز-توظيف-senior-solo-seo",
    services: "خدمات",
    terms: "الشروط",
    "vs-ai-content-tools": "مقارنة-ادوات-محتوى-ai",
    "vs-freelancer-marketplaces": "مقارنة-اسواق-المستقلين",
    "vs-generic-white-label-seo": "مقارنة-white-label-seo-عام",
    "what-is-aeo": "ما-هو-aeo",
    "what-is-geo": "ما-هو-geo",
  },
  es: {
    about: "sobre-seoh",
    accessibility: "accesibilidad",
    "acceptable-use": "uso-aceptable",
    adult: "negocios-adultos",
    agencies: "agencias",
    blog: "blog",
    blogs: "blog",
    "blog-detail": "articulo",
    "case-studies": "pruebas",
    compare: "comparar",
    contact: "contacto",
    cookies: "cookies",
    creators: "creadores",
    "editorial-policy": "politica-editorial",
    enterprise: "empresas",
    "persona-paths": "rutas-de-comprador",
    paths: "rutas",
    "multilingual-ai-search-localization": "localizacion-busqueda-ai-multilingue",
    "ai-search-governance-compliance": "gobernanza-busqueda-ai-cumplimiento",
    "scaling-seo-agency-founder": "fundador-agencia-seo-en-crecimiento",
    "white-label-seo-reseller": "revendedor-white-label-seo",
    "ppc-performance-agency": "agencia-ppc-performance",
    "web-ux-accessibility-agency": "agencia-web-ux-accesibilidad",
    "prompt-ai-content-agency": "agencia-contenido-ai-prompts",
    "multilingual-geo-reseller": "revendedor-geo-multilingue",
    "ai-video-avatar-studio": "estudio-video-ai-avatares",
    "multi-location-marketing-leader": "lider-marketing-multiubicacion",
    "franchise-geo-strategist": "estratega-geo-franquicias",
    "multi-location-reputation-operator": "operador-reputacion-multiubicacion",
    "creator-economy-growth-manager": "manager-crecimiento-economia-creadores",
    "creator-ai-video-content-team": "equipo-contenido-video-ai-creadores",
    "lawful-adult-creator-agency": "agencia-creadores-adultos-legal",
    "adult-platform-compliance-marketing": "marketing-cumplimiento-plataformas-adultas",
    "regulated-ai-visibility-governance": "gobernanza-visibilidad-ai-regulada",
    "enterprise-orm-compliance-advisor": "asesor-orm-cumplimiento-empresas",
    "adult-industry-marketing": "marketing-negocios-adultos",
    "adult-orm": "reputacion-adultos",
    "18-plus-compliance-marketing-checklist": "checklist-cumplimiento-marketing-18-plus",
    "2026-white-label-agency-playbook": "playbook-agencias-white-label-2026",
    "age-verification": "verificacion-edad",
    "ai-avatar-video": "video-avatar-ai",
    "ai-overviews-visibility-audit-template": "plantilla-auditoria-visibilidad-ai-overviews",
    "ai-ready-content-frameworks": "frameworks-contenido-listo-para-ai",
    "ai-search-product-queries": "consultas-producto-busqueda-ai",
    "ai-video-production": "produccion-video-ai",
    "ai-video-cost-time-calculator": "calculadora-costo-tiempo-video-ai",
    app: "app",
    "aup-explainer": "explicacion-aup",
    "b2b-saas-geo-aeo-visibility-audit": "auditoria-visibilidad-b2b-saas-geo-aeo",
    "blog-deatail": "correccion-articulo",
    "category-architecture": "arquitectura-categorias",
    "compliance-posture": "postura-cumplimiento",
    "content-velocity-system": "sistema-velocidad-contenido",
    "custom-lora-character-models": "modelos-personajes-lora-personalizados",
    "ecommerce-seo-audit-template": "plantilla-auditoria-ecommerce-seo",
    "fan-platform-growth-playbook": "playbook-crecimiento-plataformas-fans",
    "franchise-multi-location": "franquicia-multiubicacion",
    franchise: "franquicias",
    "geo-vs-seo": "geo-vs-seo",
    "hospitality-travel-seo-playbook": "playbook-seo-hospitalidad-viajes",
    industries: "industrias",
    insights: "articulos",
    "mainstream-platform-brand-safe-strategy": "estrategia-brand-safe-plataformas-mainstream",
    "measuring-ai-citation-success": "medir-exito-citas-ai",
    "monetization-aware-seo": "seo-orientado-a-monetizacion",
    "multi-location-local-seo-audit-template": "plantilla-auditoria-local-seo-multiubicacion",
    packages: "paquetes",
    "performer-consent": "consentimiento-performer",
    portfolio: "trayectoria",
    privacy: "privacidad",
    "privacy-policy": "politica-privacidad",
    "publisher-topic-authority": "autoridad-tematica-editorial",
    "regulated-claims-review": "revision-claims-regulados",
    resources: "recursos",
    support: "soporte",
    cancellation: "cancelacion",
    checkout: "pago",
    "manual-transfer": "transferencia-bancaria",
    "saas-tech": "saas-tecnologia",
    "schema-for-ai-engines": "schema-para-motores-ai",
    "senior-solo-seo-hiring-brief": "brief-contratacion-senior-solo-seo",
    services: "servicios",
    terms: "terminos",
    "vs-ai-content-tools": "vs-herramientas-contenido-ai",
    "vs-freelancer-marketplaces": "vs-marketplaces-freelancers",
    "vs-generic-white-label-seo": "vs-white-label-seo-generico",
    "what-is-aeo": "que-es-aeo",
    "what-is-geo": "que-es-geo",
  },
  fr: {
    about: "a-propos",
    accessibility: "accessibilite",
    "acceptable-use": "usage-acceptable",
    adult: "activites-adultes",
    agencies: "agences",
    blog: "blog",
    blogs: "blog",
    "blog-detail": "article",
    "case-studies": "preuves",
    compare: "comparer",
    contact: "contact",
    cookies: "cookies",
    creators: "createurs",
    "editorial-policy": "politique-editoriale",
    enterprise: "entreprises",
    "persona-paths": "parcours-acheteurs",
    paths: "parcours",
    "multilingual-ai-search-localization": "localisation-recherche-ai-multilingue",
    "ai-search-governance-compliance": "gouvernance-recherche-ai-conformite",
    "scaling-seo-agency-founder": "fondateur-agence-seo-en-croissance",
    "white-label-seo-reseller": "revendeur-white-label-seo",
    "ppc-performance-agency": "agence-ppc-performance",
    "web-ux-accessibility-agency": "agence-web-ux-accessibilite",
    "prompt-ai-content-agency": "agence-contenu-ai-prompts",
    "multilingual-geo-reseller": "revendeur-geo-multilingue",
    "ai-video-avatar-studio": "studio-video-ai-avatars",
    "multi-location-marketing-leader": "responsable-marketing-multi-sites",
    "franchise-geo-strategist": "strategiste-geo-franchise",
    "multi-location-reputation-operator": "operateur-reputation-multi-sites",
    "creator-economy-growth-manager": "responsable-croissance-economie-createurs",
    "creator-ai-video-content-team": "equipe-contenu-video-ai-createurs",
    "lawful-adult-creator-agency": "agence-createurs-adultes-legale",
    "adult-platform-compliance-marketing": "marketing-conformite-plateformes-adultes",
    "regulated-ai-visibility-governance": "gouvernance-visibilite-ai-reglementee",
    "enterprise-orm-compliance-advisor": "conseiller-orm-conformite-entreprise",
    "adult-industry-marketing": "marketing-activites-adultes",
    "adult-orm": "reputation-adultes",
    "18-plus-compliance-marketing-checklist": "checklist-conformite-marketing-18-plus",
    "2026-white-label-agency-playbook": "playbook-agence-white-label-2026",
    "age-verification": "verification-age",
    "ai-avatar-video": "video-avatar-ai",
    "ai-overviews-visibility-audit-template": "modele-audit-visibilite-ai-overviews",
    "ai-ready-content-frameworks": "frameworks-contenu-pret-pour-ai",
    "ai-search-product-queries": "requetes-produit-recherche-ai",
    "ai-video-production": "production-video-ai",
    "ai-video-cost-time-calculator": "calculateur-cout-temps-video-ai",
    app: "app",
    "aup-explainer": "explication-aup",
    "b2b-saas-geo-aeo-visibility-audit": "audit-visibilite-b2b-saas-geo-aeo",
    "blog-deatail": "correction-article",
    "category-architecture": "architecture-categories",
    "compliance-posture": "posture-conformite",
    "content-velocity-system": "systeme-velocite-contenu",
    "custom-lora-character-models": "modeles-personnages-lora-sur-mesure",
    "ecommerce-seo-audit-template": "modele-audit-ecommerce-seo",
    "fan-platform-growth-playbook": "playbook-croissance-plateformes-fans",
    "franchise-multi-location": "franchise-multi-sites",
    franchise: "franchise",
    "geo-vs-seo": "geo-vs-seo",
    "hospitality-travel-seo-playbook": "playbook-seo-hospitalite-voyage",
    industries: "secteurs",
    insights: "articles",
    "mainstream-platform-brand-safe-strategy": "strategie-brand-safe-plateformes-grand-public",
    "measuring-ai-citation-success": "mesurer-succes-citations-ai",
    "monetization-aware-seo": "seo-oriente-monetisation",
    "multi-location-local-seo-audit-template": "modele-audit-local-seo-multi-sites",
    packages: "offres",
    "performer-consent": "consentement-performer",
    portfolio: "parcours",
    privacy: "confidentialite",
    "privacy-policy": "politique-confidentialite",
    "publisher-topic-authority": "autorite-thematique-editeur",
    "regulated-claims-review": "revue-claims-reglementes",
    resources: "ressources",
    support: "assistance",
    cancellation: "annulation",
    checkout: "paiement",
    "manual-transfer": "virement-bancaire",
    "saas-tech": "saas-tech",
    "schema-for-ai-engines": "schema-pour-moteurs-ai",
    "senior-solo-seo-hiring-brief": "brief-recrutement-senior-solo-seo",
    services: "services",
    terms: "conditions",
    "vs-ai-content-tools": "vs-outils-contenu-ai",
    "vs-freelancer-marketplaces": "vs-marketplaces-freelances",
    "vs-generic-white-label-seo": "vs-white-label-seo-generique",
    "what-is-aeo": "definition-aeo",
    "what-is-geo": "definition-geo",
  },
  he: {
    about: "על-סאוה",
    accessibility: "נגישות",
    "acceptable-use": "שימוש-מקובל",
    adult: "עסקים-למבוגרים",
    agencies: "סוכנויות",
    blog: "בלוג",
    blogs: "בלוג",
    "blog-detail": "מאמר",
    "case-studies": "הוכחות",
    compare: "השוואה",
    contact: "יצירת-קשר",
    cookies: "קוקיז",
    creators: "יוצרים",
    enterprise: "ארגונים",
    "persona-paths": "מסלולי-קונים",
    paths: "מסלולים",
    "multilingual-ai-search-localization": "לוקליזציה-לחיפוש-ai-רב-לשוני",
    "ai-search-governance-compliance": "ממשל-חיפוש-ai-וציות",
    "scaling-seo-agency-founder": "מייסד-סוכנות-seo-צומחת",
    "white-label-seo-reseller": "משווק-white-label-seo",
    "ppc-performance-agency": "סוכנות-ppc-וביצועים",
    "web-ux-accessibility-agency": "סוכנות-ווב-ux-ונגישות",
    "prompt-ai-content-agency": "סוכנות-תוכן-ai-ופרומפטים",
    "multilingual-geo-reseller": "משווק-geo-רב-לשוני",
    "ai-video-avatar-studio": "סטודיו-וידאו-ai-ואווטארים",
    "multi-location-marketing-leader": "מנהל-שיווק-רב-מיקומי",
    "franchise-geo-strategist": "אסטרטג-geo-לזכיינות",
    "multi-location-reputation-operator": "מפעיל-מוניטין-רב-מיקומי",
    "creator-economy-growth-manager": "מנהל-צמיחה-לכלכלת-יוצרים",
    "creator-ai-video-content-team": "צוות-תוכן-וידאו-ai-ליוצרים",
    "lawful-adult-creator-agency": "סוכנות-יוצרי-מבוגרים-חוקית",
    "adult-platform-compliance-marketing": "שיווק-ציות-לפלטפורמות-מבוגרים",
    "regulated-ai-visibility-governance": "ממשל-נראות-ai-מפוקחת",
    "enterprise-orm-compliance-advisor": "יועץ-orm-וציות-לארגונים",
    "adult-industry-marketing": "שיווק-עסקים-למבוגרים",
    "adult-orm": "מוניטין-למבוגרים",
    "18-plus-compliance-marketing-checklist": "רשימת-ציות-שיווק-18-plus",
    "2026-white-label-agency-playbook": "פלייבוק-סוכנות-ווייט-לייבל-2026",
    "age-verification": "אימות-גיל",
    "ai-avatar-video": "וידאו-אווטאר-ai",
    "ai-overviews-visibility-audit-template": "תבנית-ביקורת-נראות-ai-overviews",
    "ai-ready-content-frameworks": "מסגרות-תוכן-מוכן-ai",
    "ai-search-product-queries": "שאילתות-מוצר-חיפוש-ai",
    "ai-video-production": "הפקת-וידאו-ai",
    "ai-video-cost-time-calculator": "מחשבון-עלות-זמן-וידאו-ai",
    app: "app",
    "aup-explainer": "הסבר-aup",
    "b2b-saas-geo-aeo-visibility-audit": "ביקורת-נראות-b2b-saas-geo-aeo",
    "blog-deatail": "תיקון-מאמר",
    "category-architecture": "ארכיטקטורת-קטגוריות",
    "compliance-posture": "עמדת-ציות",
    "content-velocity-system": "מערכת-מהירות-תוכן",
    "custom-lora-character-models": "מודלי-דמויות-lora-מותאמים",
    "ecommerce-seo-audit-template": "תבנית-ביקורת-ecommerce-seo",
    "fan-platform-growth-playbook": "פלייבוק-צמיחת-פלטפורמות-מעריצים",
    "franchise-multi-location": "זכיינות-רב-מיקומית",
    franchise: "זכיינות",
    "geo-vs-seo": "geo-מול-seo",
    "hospitality-travel-seo-playbook": "פלייבוק-seo-אירוח-ונסיעות",
    industries: "ענפים",
    insights: "תובנות",
    "mainstream-platform-brand-safe-strategy": "אסטרטגיה-בטוחה-למותג-בפלטפורמות",
    "measuring-ai-citation-success": "מדידת-הצלחת-ציטוטי-ai",
    "monetization-aware-seo": "seo-מבוסס-מונטיזציה",
    "multi-location-local-seo-audit-template": "תבנית-ביקורת-local-seo-רב-מיקומית",
    packages: "חבילות",
    "performer-consent": "הסכמת-מבצע",
    portfolio: "ניסיון",
    privacy: "פרטיות",
    "privacy-policy": "מדיניות-פרטיות",
    "publisher-topic-authority": "סמכות-נושאית-למוציא-לאור",
    "regulated-claims-review": "סקירת-טענות-מפוקחות",
    resources: "משאבים",
    support: "tmicha",
    cancellation: "bitul",
    checkout: "tashlum",
    "manual-transfer": "haavara-bankait",
    "saas-tech": "saas-טכנולוגיה",
    "schema-for-ai-engines": "schema-למנועי-ai",
    "senior-solo-seo-hiring-brief": "בריף-גיוס-senior-solo-seo",
    services: "שירותים",
    terms: "תנאים",
    "vs-ai-content-tools": "מול-כלי-תוכן-ai",
    "vs-freelancer-marketplaces": "מול-שווקי-פרילנסרים",
    "vs-generic-white-label-seo": "מול-white-label-seo-גנרי",
    "what-is-aeo": "מה-זה-aeo",
    "what-is-geo": "מה-זה-geo",
  },
};

SEGMENT_TRANSLATIONS.ar = {
  ...(SEGMENT_TRANSLATIONS.ar ?? {}),
  "editorial-policy": "siyasat-tahririya",
};
SEGMENT_TRANSLATIONS.he = {
  ...(SEGMENT_TRANSLATIONS.he ?? {}),
  "editorial-policy": "mediniyut-arikha",
};

const WORD_TRANSLATIONS: Partial<Record<NonDefaultLocale, Record<string, string>>> = {
  ru: {
    adult: "adult",
    agency: "agentstvo",
    avatar: "avatar",
    brand: "brend",
    brands: "brendy",
    building: "sozdanie",
    calculator: "kalkulyator",
    cadence: "kadentsiya",
    category: "kategoriya",
    checklist: "cheklist",
    citation: "tsitirovanie",
    compliance: "komplaens",
    consumer: "potrebitelskie",
    content: "kontent",
    creator: "kreator",
    creators: "kreatory",
    custom: "kastomnye",
    distribution: "distributsiya",
    ecommerce: "ecommerce",
    economy: "ekonomika",
    education: "obrazovanie",
    engines: "dvizhki",
    entity: "sushchnost",
    events: "sobytiya",
    faceted: "fasetnaya",
    fan: "fan",
    freelancer: "frilanser",
    generic: "tipovoy",
    growth: "rost",
    guide: "rukovodstvo",
    health: "zdorove",
    hospitality: "gostepriimstvo",
    implementation: "vnedrenie",
    industrial: "promyshlennost",
    local: "lokalnyy",
    management: "upravlenie",
    margin: "marzha",
    marketing: "marketing",
    measuring: "izmerenie",
    media: "media",
    medical: "meditsinskiy",
    model: "model",
    models: "modeli",
    monetization: "monetizatsiya",
    multi: "multi",
    navigation: "navigatsiya",
    onboarding: "onbording",
    optimization: "optimizatsiya",
    overviews: "obzory",
    page: "stranitsa",
    payment: "platezhi",
    performance: "effektivnost",
    platform: "platforma",
    policy: "politika",
    processor: "processor",
    product: "produkt",
    professional: "professionalnye",
    proof: "dokazatelstva",
    promotion: "prodvizhenie",
    publisher: "izdatel",
    publishers: "izdateli",
    ready: "gotovyy",
    readiness: "gotovnost",
    regulated: "reguliruemye",
    reputation: "reputatsiya",
    reporting: "otchetnost",
    review: "obzor",
    safe: "bezopasno",
    schema: "schema",
    search: "poisk",
    senior: "senior",
    services: "uslugi",
    shorts: "shorts",
    strategy: "strategiya",
    tech: "tekhnologii",
    technical: "tekhnicheskiy",
    template: "shablon",
    tools: "instrumenty",
    topic: "tema",
    tracking: "otslezhivanie",
    travel: "puteshestviya",
    use: "ispolzovanie",
    velocity: "skorost",
    visibility: "vidimost",
    wellness: "wellness",
    what: "chto",
    franchise: "franshiza",
    industry: "industriya",
    location: "lokatsiya",
    production: "proizvodstvo",
    video: "video",
    white: "white",
    label: "label",
    zero: "zero",
    click: "klik",
  },
  ar: {
    adult: "بالغين",
    agency: "وكالة",
    avatar: "افتار",
    brand: "علامة",
    brands: "علامات",
    building: "بناء",
    calculator: "حاسبة",
    cadence: "وتيرة",
    category: "تصنيف",
    checklist: "قائمة",
    citation: "استشهاد",
    compliance: "امتثال",
    consumer: "مستهلك",
    content: "محتوى",
    creator: "صانع",
    creators: "صناع",
    custom: "مخصص",
    distribution: "توزيع",
    ecommerce: "تجارة-الكترونية",
    economy: "اقتصاد",
    education: "تعليم",
    engines: "محركات",
    entity: "كيان",
    events: "فعاليات",
    faceted: "مرشحات",
    fan: "معجبين",
    freelancer: "مستقل",
    generic: "عام",
    growth: "نمو",
    guide: "دليل",
    health: "صحة",
    hospitality: "ضيافة",
    implementation: "تنفيذ",
    industrial: "صناعي",
    local: "محلي",
    management: "ادارة",
    margin: "هامش",
    marketing: "تسويق",
    measuring: "قياس",
    media: "اعلام",
    medical: "طبي",
    model: "نموذج",
    models: "نماذج",
    monetization: "ربح",
    multi: "متعدد",
    navigation: "تنقل",
    onboarding: "تهيئة",
    optimization: "تحسين",
    overviews: "ملخصات",
    page: "صفحة",
    payment: "دفع",
    performance: "اداء",
    platform: "منصة",
    policy: "سياسة",
    processor: "معالج",
    product: "منتج",
    professional: "مهني",
    proof: "اثبات",
    promotion: "ترويج",
    publisher: "ناشر",
    publishers: "ناشرين",
    ready: "جاهز",
    readiness: "جاهزية",
    regulated: "منظم",
    reputation: "سمعة",
    reporting: "تقارير",
    review: "مراجعة",
    safe: "امن",
    schema: "سكيما",
    search: "بحث",
    senior: "خبير",
    services: "خدمات",
    shorts: "شورتس",
    strategy: "استراتيجية",
    tech: "تقنية",
    technical: "تقني",
    template: "قالب",
    tools: "ادوات",
    topic: "موضوع",
    tracking: "تتبع",
    travel: "سفر",
    use: "استخدام",
    velocity: "سرعة",
    visibility: "ظهور",
    wellness: "عافية",
    what: "ما",
    franchise: "امتياز",
    industry: "قطاع",
    location: "موقع",
    production: "انتاج",
    video: "فيديو",
    white: "وايت",
    label: "ليبل",
    zero: "صفر",
    click: "نقرة",
  },
  es: {
    adult: "adultos",
    agency: "agencia",
    avatar: "avatar",
    brand: "marca",
    brands: "marcas",
    building: "construccion",
    calculator: "calculadora",
    cadence: "cadencia",
    category: "categoria",
    checklist: "checklist",
    citation: "citas",
    compliance: "cumplimiento",
    consumer: "consumo",
    content: "contenido",
    creator: "creador",
    creators: "creadores",
    custom: "personalizado",
    distribution: "distribucion",
    ecommerce: "ecommerce",
    economy: "economia",
    education: "educacion",
    engines: "motores",
    entity: "entidad",
    events: "eventos",
    faceted: "facetas",
    fan: "fans",
    freelancer: "freelancer",
    generic: "generico",
    growth: "crecimiento",
    guide: "guia",
    health: "salud",
    hospitality: "hospitalidad",
    implementation: "implementacion",
    industrial: "industrial",
    local: "local",
    management: "gestion",
    margin: "margen",
    marketing: "marketing",
    measuring: "medicion",
    media: "medios",
    medical: "medico",
    model: "modelo",
    models: "modelos",
    monetization: "monetizacion",
    multi: "multi",
    navigation: "navegacion",
    onboarding: "onboarding",
    optimization: "optimizacion",
    overviews: "resumenes",
    page: "pagina",
    payment: "pagos",
    performance: "rendimiento",
    platform: "plataforma",
    policy: "politica",
    processor: "procesador",
    product: "producto",
    professional: "profesional",
    proof: "prueba",
    promotion: "promocion",
    publisher: "editorial",
    publishers: "editoriales",
    ready: "preparado",
    readiness: "preparacion",
    regulated: "regulado",
    reputation: "reputacion",
    reporting: "reporting",
    review: "revision",
    safe: "seguro",
    schema: "schema",
    search: "busqueda",
    senior: "senior",
    services: "servicios",
    shorts: "shorts",
    strategy: "estrategia",
    tech: "tecnologia",
    technical: "tecnico",
    template: "plantilla",
    tools: "herramientas",
    topic: "tema",
    tracking: "seguimiento",
    travel: "viajes",
    use: "uso",
    velocity: "velocidad",
    visibility: "visibilidad",
    wellness: "bienestar",
    what: "que-es",
    franchise: "franquicia",
    industry: "industria",
    location: "ubicacion",
    production: "produccion",
    video: "video",
    white: "white",
    label: "label",
    zero: "cero",
    click: "clic",
  },
  fr: {
    adult: "adultes",
    agency: "agence",
    avatar: "avatar",
    brand: "marque",
    brands: "marques",
    building: "construction",
    calculator: "calculateur",
    cadence: "cadence",
    category: "categorie",
    checklist: "checklist",
    citation: "citations",
    compliance: "conformite",
    consumer: "consommateur",
    content: "contenu",
    creator: "createur",
    creators: "createurs",
    custom: "sur-mesure",
    distribution: "distribution",
    ecommerce: "ecommerce",
    economy: "economie",
    education: "education",
    engines: "moteurs",
    entity: "entite",
    events: "evenements",
    faceted: "facettes",
    fan: "fans",
    freelancer: "freelance",
    generic: "generique",
    growth: "croissance",
    guide: "guide",
    health: "sante",
    hospitality: "hospitalite",
    implementation: "implementation",
    industrial: "industriel",
    local: "local",
    management: "gestion",
    margin: "marge",
    marketing: "marketing",
    measuring: "mesure",
    media: "medias",
    medical: "medical",
    model: "modele",
    models: "modeles",
    monetization: "monetisation",
    multi: "multi",
    navigation: "navigation",
    onboarding: "onboarding",
    optimization: "optimisation",
    overviews: "apercus",
    page: "page",
    payment: "paiement",
    performance: "performance",
    platform: "plateforme",
    policy: "politique",
    processor: "processeur",
    product: "produit",
    professional: "professionnel",
    proof: "preuve",
    promotion: "promotion",
    publisher: "editeur",
    publishers: "editeurs",
    ready: "pret",
    readiness: "preparation",
    regulated: "reglemente",
    reputation: "reputation",
    reporting: "reporting",
    review: "revue",
    safe: "sur",
    schema: "schema",
    search: "recherche",
    senior: "senior",
    services: "services",
    shorts: "shorts",
    strategy: "strategie",
    tech: "tech",
    technical: "technique",
    template: "modele",
    tools: "outils",
    topic: "theme",
    tracking: "suivi",
    travel: "voyage",
    use: "usage",
    velocity: "vitesse",
    visibility: "visibilite",
    wellness: "bien-etre",
    what: "definition",
    franchise: "franchise",
    industry: "secteur",
    location: "site",
    production: "production",
    video: "video",
    white: "white",
    label: "label",
    zero: "zero",
    click: "clic",
  },
  he: {
    adult: "מבוגרים",
    agency: "סוכנות",
    avatar: "אווטאר",
    brand: "מותג",
    brands: "מותגים",
    building: "בניית",
    calculator: "מחשבון",
    cadence: "קצב",
    category: "קטגוריה",
    checklist: "רשימת-בדיקה",
    citation: "ציטוטים",
    compliance: "ציות",
    consumer: "צרכנים",
    content: "תוכן",
    creator: "יוצר",
    creators: "יוצרים",
    custom: "מותאם",
    distribution: "הפצה",
    ecommerce: "איקומרס",
    economy: "כלכלה",
    education: "חינוך",
    engines: "מנועים",
    entity: "ישות",
    events: "אירועים",
    faceted: "סינון",
    fan: "מעריצים",
    freelancer: "פרילנסר",
    generic: "גנרי",
    growth: "צמיחה",
    guide: "מדריך",
    health: "בריאות",
    hospitality: "אירוח",
    implementation: "יישום",
    industrial: "תעשייה",
    local: "מקומי",
    management: "ניהול",
    margin: "מרווח",
    marketing: "שיווק",
    measuring: "מדידה",
    media: "מדיה",
    medical: "רפואי",
    model: "מודל",
    models: "מודלים",
    monetization: "מוניטיזציה",
    multi: "רב",
    navigation: "ניווט",
    onboarding: "אונבורדינג",
    optimization: "אופטימיזציה",
    overviews: "סקירות",
    page: "עמוד",
    payment: "תשלום",
    performance: "ביצועים",
    platform: "פלטפורמה",
    policy: "מדיניות",
    processor: "מעבד",
    product: "מוצר",
    professional: "מקצועי",
    proof: "הוכחה",
    promotion: "קידום",
    publisher: "מו״ל",
    publishers: "מוציאים-לאור",
    ready: "מוכן",
    readiness: "מוכנות",
    regulated: "מפוקח",
    reputation: "מוניטין",
    reporting: "דיווח",
    review: "סקירה",
    safe: "בטוח",
    schema: "סכימה",
    search: "חיפוש",
    senior: "בכיר",
    services: "שירותים",
    shorts: "שורטס",
    strategy: "אסטרטגיה",
    tech: "טכנולוגיה",
    technical: "טכני",
    template: "תבנית",
    tools: "כלים",
    topic: "נושא",
    tracking: "מעקב",
    travel: "נסיעות",
    use: "שימוש",
    velocity: "מהירות",
    visibility: "נראות",
    wellness: "וולנס",
    what: "מה-זה",
    franchise: "זכיינות",
    industry: "ענף",
    location: "מיקום",
    production: "הפקה",
    video: "וידאו",
    white: "ווייט",
    label: "לייבל",
    zero: "אפס",
    click: "קליק",
  },
};

const ROUTE_PATHS = getCanonicalRouteDefinitions()
  .filter((route) => route.indexable)
  .map((route) => route.path);

const LOCALIZED_BY_ROUTE = buildLocalizedRouteMap();
const ROUTE_BY_LOCALIZED = buildReverseRouteMap(LOCALIZED_BY_ROUTE);
const LEGACY_NATIVE_ROUTE_BY_LOCALIZED = buildLegacyNativeReverseRouteMap();
const LEGACY_LOCALIZED_ROUTE_ALIASES: Partial<Record<AppLocale, Record<string, string>>> = {
  ru: {
    "/franshiza/tema-ealtd-multi-tema-ealtd": "/franchise/franchise-multi-location",
  },
  ar: {
    "/امتياز/موضوع-ealtd-متعدد-موضوع-ealtd": "/franchise/franchise-multi-location",
  },
  es: {
    "/franquicias/tema-ealtd-multi-tema-ealtd": "/franchise/franchise-multi-location",
  },
  fr: {
    "/franchise/sujet-ealtd-multi-sujet-ealtd": "/franchise/franchise-multi-location",
  },
  he: {
    "/זכיינות/נושא-ealtd-רב-נושא-ealtd": "/franchise/franchise-multi-location",
  },
};

export function localizedSlugPath(locale: string, routePath = "/"): string {
  const appLocale = toAppLocale(locale) ?? DEFAULT_LOCALE;
  const canonicalRoutePath = normalizeRoutePath(routePath);

  if (appLocale === DEFAULT_LOCALE) {
    return canonicalRoutePath;
  }

  return LOCALIZED_BY_ROUTE[appLocale].get(canonicalRoutePath) ?? translatePath(appLocale, canonicalRoutePath);
}

export function localizedPathForRoute(locale: string, routePath = "/"): string {
  const appLocale = toAppLocale(locale) ?? DEFAULT_LOCALE;
  const slugPath = localizedSlugPath(appLocale, routePath);

  return slugPath === "/" ? `/${appLocale}` : `/${appLocale}${slugPath}`;
}

export function ampPathForRoute(locale: string, routePath = "/"): string {
  const appLocale = toAppLocale(locale) ?? DEFAULT_LOCALE;
  const localizedPath = localizedPathForRoute(appLocale, routePath);

  return `/amp${localizedPath}`;
}

export function canonicalRoutePathFromLocalizedPath(path = "/"): string {
  const normalizedPath = normalizeUrlPath(path);
  const ampStripped = stripAmpPrefix(normalizedPath);
  const parsed = parseLocalePath(ampStripped);

  if (!parsed) {
    return normalizeRoutePath(ampStripped);
  }

  if (parsed.locale === DEFAULT_LOCALE) {
    return normalizeRoutePath(parsed.routeLikePath);
  }

  return routePathForLocalizedSlug(parsed.locale, parsed.routeLikePath) ?? normalizeRoutePath(parsed.routeLikePath);
}

export function routePathForLocalizedSlug(locale: AppLocale, slugPath = "/"): string | null {
  const routeLikePath = normalizeRoutePath(decodePath(slugPath));

  if (locale === DEFAULT_LOCALE) {
    return routeLikePath;
  }

  return ROUTE_BY_LOCALIZED[locale].get(routeLikePath) ?? LEGACY_NATIVE_ROUTE_BY_LOCALIZED[locale]?.get(routeLikePath) ?? null;
}

export function resolveLocalizedSlugRouting(pathname: string): LocalizedSlugRoutingResolution | null {
  const normalizedPath = normalizeUrlPath(pathname);
  const normalizedDecodedPath = normalizeUrlPath(decodePath(pathname));
  const parsed = parseLocalePath(normalizedPath);

  if (!parsed || parsed.locale === DEFAULT_LOCALE) {
    return null;
  }

  const legacyRoutePath = legacyRoutePathForLocalizedAlias(parsed.locale, parsed.routeLikePath);
  if (legacyRoutePath && isKnownPublicRoute(legacyRoutePath)) {
    return {
      type: "redirect",
      locale: parsed.locale,
      routePath: legacyRoutePath,
      destinationPath: localizedPathForRoute(parsed.locale, legacyRoutePath),
      status: 301,
    };
  }

  const routePath = routePathForLocalizedSlug(parsed.locale, parsed.routeLikePath) ?? normalizeRoutePath(parsed.routeLikePath);
  const localizedDestinationPath = localizedPathForRoute(parsed.locale, routePath);

  if (localizedDestinationPath !== normalizedDecodedPath && isKnownPublicRoute(routePath)) {
    return {
      type: "redirect",
      locale: parsed.locale,
      routePath,
      destinationPath: localizedDestinationPath,
      status: 301,
    };
  }

  if (isKnownPublicRoute(routePath)) {
    const destinationPath = routePath === "/" ? `/${parsed.locale}` : `/${parsed.locale}${routePath}`;

    if (destinationPath !== normalizedDecodedPath) {
      return {
        type: "rewrite",
        locale: parsed.locale,
        routePath,
        destinationPath,
      };
    }
  }

  return null;
}

export function shouldRedirectAmpSlug(locale: AppLocale, requestedRouteLikePath: string): boolean {
  if (locale === DEFAULT_LOCALE) {
    return false;
  }

  const routePath = normalizeRoutePath(requestedRouteLikePath);

  return isKnownPublicRoute(routePath) && localizedSlugPath(locale, routePath) !== routePath;
}

export function getLocalizedRoutePaths(locale: AppLocale = DEFAULT_LOCALE): Record<string, string> {
  return Object.fromEntries(LOCALIZED_BY_ROUTE[locale].entries());
}

function buildLocalizedRouteMap(): Record<AppLocale, Map<string, string>> {
  const maps = Object.fromEntries(ALL_APP_LOCALES.map((locale) => [locale, new Map<string, string>()])) as Record<
    AppLocale,
    Map<string, string>
  >;

  for (const path of ROUTE_PATHS) {
    const routePath = normalizeRoutePath(path);

    for (const locale of ALL_APP_LOCALES) {
      maps[locale].set(routePath, locale === DEFAULT_LOCALE ? routePath : translatePath(locale, routePath));
    }
  }

  validateLocalizedRouteMap(maps);

  return maps;
}

function buildReverseRouteMap(maps: Record<AppLocale, Map<string, string>>): Record<AppLocale, Map<string, string>> {
  return Object.fromEntries(
    ALL_APP_LOCALES.map((locale) => [
      locale,
      new Map([...maps[locale].entries()].map(([routePath, localizedPath]) => [localizedPath, routePath])),
    ]),
  ) as Record<AppLocale, Map<string, string>>;
}

function buildLegacyNativeReverseRouteMap(): Partial<Record<AppLocale, Map<string, string>>> {
  return Object.fromEntries(
    LEGACY_NATIVE_SLUG_LOCALES.map((locale) => [
      locale,
      new Map(
        ROUTE_PATHS.map((path) => {
          const routePath = normalizeRoutePath(path);
          return [translatePathWithPolicy(locale, routePath, false), routePath] as const;
        }).filter(([localizedPath, routePath]) => localizedPath !== routePath),
      ),
    ]),
  ) as Partial<Record<AppLocale, Map<string, string>>>;
}

function translatePath(locale: AppLocale, routePath: string): string {
  return translatePathWithPolicy(locale, routePath, true);
}

function translatePathWithPolicy(locale: AppLocale, routePath: string, applyEnglishSlugPolicy: boolean): string {
  const normalizedRoutePath = normalizeRoutePath(routePath);

  if (
    locale === DEFAULT_LOCALE ||
    normalizedRoutePath === "/" ||
    (applyEnglishSlugPolicy && ENGLISH_SLUG_LOCALES.has(locale))
  ) {
    return normalizedRoutePath;
  }

  const nonDefaultLocale = locale as NonDefaultLocale;

  return `/${normalizedRoutePath
    .split("/")
    .filter(Boolean)
    .map((segment) => translateSegment(nonDefaultLocale, segment))
    .join("/")}`;
}

function translateSegment(locale: NonDefaultLocale, segment: string): string {
  const normalizedSegment = segment.trim().toLowerCase();

  if (/^\d+$/.test(normalizedSegment) || PROTECTED_SEGMENTS.has(normalizedSegment)) {
    return normalizedSegment;
  }

  const directTranslation = SEGMENT_TRANSLATIONS[locale]?.[normalizedSegment];
  if (directTranslation) {
    return directTranslation;
  }

  const words = normalizedSegment.split("-").filter(Boolean);
  const translatedWords = words.map((word) => translateWord(locale, word));
  const translatedSegment = translatedWords.join("-");

  return translatedSegment || localizedFallbackSegment(locale, normalizedSegment);
}

function translateWord(locale: NonDefaultLocale, word: string): string {
  if (/^\d+$/.test(word) || PROTECTED_SEGMENTS.has(word)) {
    return word;
  }

  return WORD_TRANSLATIONS[locale]?.[word] ?? word;
}

function localizedFallbackSegment(locale: NonDefaultLocale, sourceSegment: string): string {
  const hash = shortHash(sourceSegment);

  switch (locale) {
    case "ru":
      return `tema-${hash}`;
    case "ar":
      return `موضوع-${hash}`;
    case "es":
      return `tema-${hash}`;
    case "fr":
      return `sujet-${hash}`;
    case "he":
      return `נושא-${hash}`;
    case "de":
      return `thema-${hash}`;
    case "pt-br":
      return `topico-${hash}`;
    case "ja":
      return `kiji-${hash}`;
    case "hi":
      return `vishay-${hash}`;
    case "id":
      return `topik-${hash}`;
    case "zh-cn":
      return `zhuti-${hash}`;
    case "tr":
      return `konu-${hash}`;
    case "ko":
      return `juje-${hash}`;
    case "it":
      return `tema-${hash}`;
    default:
      return `slug-${hash}`;
  }
}

function validateLocalizedRouteMap(maps: Record<AppLocale, Map<string, string>>): void {
  for (const locale of ALL_APP_LOCALES) {
    const byLocalizedPath = new Map<string, string>();

    for (const [routePath, localizedPath] of maps[locale]) {
      const duplicate = byLocalizedPath.get(localizedPath);

      if (duplicate) {
        throw new Error(`Duplicate localized slug for ${locale}: ${localizedPath} maps ${duplicate} and ${routePath}.`);
      }

      byLocalizedPath.set(localizedPath, routePath);

      if (locale !== DEFAULT_LOCALE && !ENGLISH_SLUG_LOCALES.has(locale)) {
        assertNoEnglishFallbackSegments(locale as NonDefaultLocale, routePath, localizedPath);
      }

      for (const segment of localizedPath.split("/").filter(Boolean)) {
        if (segment.includes("?") || segment.includes("#") || segment.includes("%2f")) {
          throw new Error(`Unsafe localized slug segment for ${locale} ${routePath}: ${segment}.`);
        }
      }
    }
  }
}

function assertNoEnglishFallbackSegments(locale: NonDefaultLocale, routePath: string, localizedPath: string): void {
  const sourceSegments = routePath.split("/").filter(Boolean);
  const localizedSegments = localizedPath.split("/").filter(Boolean);

  sourceSegments.forEach((sourceSegment, index) => {
    const localizedSegment = localizedSegments[index];

    if (!localizedSegment) {
      throw new Error(`Missing localized slug segment for ${locale} ${routePath}.`);
    }

    if (
      index === 0 &&
      sourceSegment === localizedSegment &&
      !isProtectedSourceSegment(sourceSegment) &&
      !hasExplicitLocalizedTranslation(locale, sourceSegment)
    ) {
      throw new Error(`English fallback slug segment for ${locale} ${routePath}: ${sourceSegment}.`);
    }
  });
}

function hasExplicitLocalizedTranslation(locale: NonDefaultLocale, segment: string): boolean {
  if (SEGMENT_TRANSLATIONS[locale]?.[segment]) {
    return true;
  }

  return segment
    .split("-")
    .filter(Boolean)
    .every((word) => Boolean(WORD_TRANSLATIONS[locale]?.[word]) || PROTECTED_SEGMENTS.has(word) || /^\d+$/.test(word));
}

function isProtectedSourceSegment(segment: string): boolean {
  return segment
    .split("-")
    .filter(Boolean)
    .every((word) => /^\d+$/.test(word) || PROTECTED_SEGMENTS.has(word));
}

function isKnownPublicRoute(routePath: string): boolean {
  return LOCALIZED_BY_ROUTE[DEFAULT_LOCALE].has(normalizeRoutePath(routePath));
}

function legacyRoutePathForLocalizedAlias(locale: AppLocale, routeLikePath: string): string | null {
  return LEGACY_LOCALIZED_ROUTE_ALIASES[locale]?.[normalizeRoutePath(routeLikePath)] ?? null;
}

function parseLocalePath(pathname: string): { locale: AppLocale; routeLikePath: string } | null {
  const decodedPath = decodePath(pathname);
  const segments = decodedPath.split("/").filter(Boolean);
  const locale = toAppLocale(segments[0]);

  if (!locale) {
    return null;
  }

  const routeLikePath = segments.length === 1 ? "/" : `/${segments.slice(1).join("/")}`;

  return {
    locale,
    routeLikePath: normalizeRoutePath(routeLikePath),
  };
}

function stripAmpPrefix(pathname: string): string {
  const decodedPath = decodePath(pathname);

  for (const locale of ALL_APP_LOCALES) {
    if (decodedPath === `/amp/${locale}`) {
      return `/${locale}`;
    }

    if (decodedPath.startsWith(`/amp/${locale}/`)) {
      return decodedPath.slice("/amp".length);
    }
  }

  return decodedPath;
}

function normalizeUrlPath(path = "/"): string {
  const pathname = pathnameFromInput(path);
  const prefixed = pathname.startsWith("/") ? pathname : `/${pathname}`;
  const withoutTrailingSlash = prefixed.length > 1 ? prefixed.replace(/\/+$/, "") : prefixed;

  return withoutTrailingSlash || "/";
}

function normalizeRoutePath(path = "/"): string {
  const normalized = normalizeUrlPath(path);

  if (normalized === `/${DEFAULT_LOCALE}`) {
    return "/";
  }

  return normalized;
}

function pathnameFromInput(value: string): string {
  try {
    return new URL(value).pathname;
  } catch {
    return value.split(/[?#]/, 1)[0] || "/";
  }
}

function decodePath(path: string): string {
  try {
    return decodeURI(path);
  } catch {
    return path;
  }
}

function toAppLocale(value: string | undefined): AppLocale | null {
  return ALL_APP_LOCALES.includes(value as AppLocale) ? (value as AppLocale) : null;
}

function shortHash(value: string): string {
  let hash = 0;

  for (let index = 0; index < value.length; index += 1) {
    hash = (hash * 31 + value.charCodeAt(index)) >>> 0;
  }

  return hash.toString(36).slice(0, 5);
}
